import { useState } from 'react';
import { JsonEditor } from '../../common/JsonEditor';
import { HeadersEditor } from '../../common/HeadersEditor';

function parseHeaderList(text: string): string[] | undefined {
  const names = text.split(',').map((name) => name.trim()).filter(Boolean);
  return names.length > 0 ? names : undefined;
}

interface HttpRequestConfigProps {
  config: Record<string, unknown>;
  onChange: (config: Record<string, unknown>) => void;
}

export function HttpRequestConfig({ config, onChange }: HttpRequestConfigProps) {
  const url = (config.url as string) || '';
  const method = (config.method as string) || 'GET';
  const headers = (typeof config.headers === 'object' && config.headers
    ? config.headers
    : null) as Record<string, string> | null;
  const body = typeof config.body === 'object' && config.body
    ? JSON.stringify(config.body, null, 2)
    : (config.body as string) || '';
  const [forwardHeadersText, setForwardHeadersText] = useState(() =>
    Array.isArray(config.forwardHeaders) ? config.forwardHeaders.join(', ') : '',
  );
  const applyResponse = (config.applyResponse as boolean) || false;
  const timeoutMs = (config.timeoutMs as number) || 5000;
  const onError = (config.onError as string) || 'continue';

  const update = (key: string, value: unknown) => {
    onChange({ ...config, [key]: value });
  };

  return (
    <div className="space-y-4">
      <div className="fieldset">
        <label className="fieldset-legend">URL</label>
        <input
          type="text"
          value={url}
          onChange={(e) => update('url', e.target.value)}
          placeholder="https://api.example.com/webhook"
          className="input w-full"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="fieldset min-w-0">
          <label className="fieldset-legend">Method</label>
          <select
            value={method}
            onChange={(e) => update('method', e.target.value)}
            className="select w-full"
          >
            {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>

        <div className="fieldset min-w-0">
          <label className="fieldset-legend">Timeout (ms)</label>
          <input
            type="number"
            value={timeoutMs}
            onChange={(e) => update('timeoutMs', parseInt(e.target.value, 10) || 5000)}
            min="100"
            max="30000"
            className="input w-full"
          />
        </div>
      </div>

      <div className="fieldset">
        <label className="fieldset-legend">Headers</label>
        <HeadersEditor
          value={headers}
          onChange={(h) => update('headers', h)}
          placeholder={{ key: 'Authorization', value: 'Bearer token' }}
        />
      </div>

      <div className="fieldset">
        <label className="fieldset-legend" htmlFor="http-request-forward-headers">Forward request headers</label>
        <input
          id="http-request-forward-headers"
          type="text"
          value={forwardHeadersText}
          onChange={(e) => {
            // Keep the raw text so a trailing comma survives while typing.
            setForwardHeadersText(e.target.value);
            update('forwardHeaders', parseHeaderList(e.target.value));
          }}
          placeholder="X-Request-ID, Accept-Language"
          className="input w-full"
        />
        <p className="label whitespace-normal text-base-content/60">
          Incoming headers aren't forwarded unless listed here. Credential headers (Authorization,
          Cookie, API keys) are never forwarded.
        </p>
      </div>

      <div className="fieldset">
        <label className="fieldset-legend">Body (JSON)</label>
        <JsonEditor
          value={body}
          onChange={(v) => {
            try {
              update('body', JSON.parse(v));
            } catch {
              update('body', v);
            }
          }}
          placeholder='{"key": "value"}'
          rows={3}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="fieldset min-w-0">
          <label className="fieldset-legend">On Error</label>
          <select
            value={onError}
            onChange={(e) => update('onError', e.target.value)}
            className="select w-full"
          >
            <option value="continue">Continue</option>
            <option value="stop">Stop Pipeline</option>
          </select>
        </div>

        <div className="fieldset min-w-0">
          <label className="fieldset-legend">Apply Response</label>
          <label className="flex items-center gap-2 mt-2 cursor-pointer justify-self-start">
            <input
              type="checkbox"
              checked={applyResponse}
              onChange={(e) => update('applyResponse', e.target.checked)}
              className="checkbox checkbox-primary"
            />
            <span className="text-sm text-base-content/80">Use HTTP response as endpoint response</span>
          </label>
        </div>
      </div>
    </div>
  );
}
